import React from 'react';
import {
	Breakpoints,
	Container,
	ContainerPadding,
	CTA,
	Emphasis,
	Select,
	Text,
	TextInput,
	TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import styled from 'styled-components';
import { CtaType } from '@esportal/portal-service';
import { useTrackingManager } from '../../../tracking/use-tracking-manager';
import { PortalContext } from '../../../context/portal-context';
import { formatOwnership } from '../../../tracking/tracking-helper';

// Container created to ensure that Select and Input will be overflowing
const BorderStyledContainer = styled.div`
	border: 1px solid transparent;

	& select {
		font-size: 20px;
		font-weight: 700;
	}
`;

export const CTAStyledNextQuestion = styled(CTA as React.ComponentType<any>)`
	color: ${(props: any) => (props.disabled ? '#3c484d' : props.emphasis === 'primary' ? 'white' : '#00b0f0')};
	background-color: ${(props: any) =>
		props.disabled ? '#dfe4e8' : props.emphasis === 'primary' ? '#00b0f0' : 'white'};
	border: 1px solid ${(props: any) => (props.disabled ? '#dfe4e8' : 'white')};
	z-index: 100;
`;

const StyledContainer = styled.div<{ numberOfCTAs: number }>`
	display: flex;
	flex-direction: column;
	gap: 24px;

	@media (min-width: 960px) {
		display: grid;
		grid-template-columns: repeat(2, 1fr);
		gap: 40px;
	}

	@media (min-width: 1280px) {
		grid-template-columns: ${(props) => (props.numberOfCTAs < 6 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)')};
		padding-right: ${(props) => props.numberOfCTAs < 6 && 'var(--size-grid005)'};
	}

	@media (min-width: 1920px) {
		padding-right: ${(props) => props.numberOfCTAs < 6 && 'var(--size-grid005)'};
	}

	@media (min-width: 2560px) {
		padding-right: ${(props) => props.numberOfCTAs < 6 && 'var(--size-grid004)'};
	}
`;

export const DataCollectionCTAs: React.FC<{
	type: CtaType;
	ctaAnswers: { label: string; value: string; stepsToShow?: string[] }[];
	updateVisibleSteps: (steps: string[], isSelected: boolean) => void;
	handleLinealNextStep: (answer?: string[]) => void;
	handleAnswerChange: (value: string[]) => void;
	stepName: string;
	userLevel: number;
	selectLabel?: string;
	isLabelFloating?: boolean;
	defaultValue?: string;
}> = ({
	ctaAnswers,
	type,
	updateVisibleSteps,
	handleLinealNextStep,
	handleAnswerChange,
	stepName,
	userLevel,
	selectLabel,
	isLabelFloating,
}) => {
	const { portalService } = React.useContext(PortalContext);

	const [emphases, setEmphases] = React.useState(ctaAnswers.map(() => 'secondary' as Emphasis));
	const selectedValuesRef = React.useRef<string[]>([]);
	const userVehicles = portalService.getPersonVehicles().getValue();
	const trackingManager = useTrackingManager();

	React.useEffect(() => {
		if (emphases.length !== ctaAnswers.length) {
			setEmphases(ctaAnswers.map(() => 'secondary' as Emphasis));
		}
	}, [ctaAnswers]);

	const handleClick = (index: number, event: React.MouseEvent<HTMLButtonElement>) => {
		if (type === CtaType.CHECKBOX) {
			const isSelected = emphases[index] === 'secondary';

			setEmphases((prev) =>
				prev.map((emphasis, i) => (i === index ? (isSelected ? 'primary' : 'secondary') : emphasis)),
			);

			if (ctaAnswers[index].stepsToShow) {
				updateVisibleSteps(ctaAnswers[index].stepsToShow!, isSelected);
			}

			if (isSelected) {
				selectedValuesRef.current = [...selectedValuesRef.current, ctaAnswers[index].value];
			} else {
				selectedValuesRef.current = selectedValuesRef.current.filter((value) => value !== ctaAnswers[index].value);
			}

			handleAnswerChange(selectedValuesRef.current);
		} else if (type === CtaType.RADIO) {
			handleLinealNextStep([ctaAnswers[index].value]);
			trackingManager.trackClubVWFormButtonClick(
				{
					FormName: `vw-club-data_collection-${userLevel}`,
					FormStart: false,
					FormTypeMultiple: ['otherform'],
				},
				{ contentId: `Answered step: ${stepName}` },
				{ vehicle: formatOwnership(userVehicles?.vehicles) },
			);
		}

		event.currentTarget.blur();
	};

	return (
		<Container
			padding={{
				top: { [Breakpoints.default]: ContainerPadding.static500, [Breakpoints.b960]: ContainerPadding.static520 },
			}}
			stretchContent
		>
			{type === CtaType.SELECT ? (
				<Container
					stretchContent
					padding={{
						bottom: {
							[Breakpoints.default]: ContainerPadding.static500,
							[Breakpoints.b960]: ContainerPadding.static520,
						},
					}}
				>
					<BorderStyledContainer>
						<Select
							defaultValue=""
							onChange={(event: React.SyntheticEvent<HTMLSelectElement, Event>) =>
								handleAnswerChange([event.currentTarget.value])
							}
							label={selectLabel}
							isFloating={isLabelFloating}
						>
							{ctaAnswers.map((cta, index) => (
								<option key={index} value={cta.value} disabled={cta.value === '' && true}>
									<Text appearance={TokenTextAppearance.bigcopy200} bold>
										{cta.label}
									</Text>
								</option>
							))}
						</Select>
					</BorderStyledContainer>
				</Container>
			) : type === CtaType.INPUT ? (
				<Container stretchContent>
					<BorderStyledContainer>
						<TextInput
							placeholder={ctaAnswers[0].label}
							onChange={(event: React.SyntheticEvent<HTMLInputElement, Event>) =>
								handleAnswerChange([event.currentTarget.value])
							}
						/>
					</BorderStyledContainer>
				</Container>
			) : (
				<Container stretchContent>
					<StyledContainer className="styled-container-form" numberOfCTAs={ctaAnswers.length}>
						{ctaAnswers.map((cta, index) => (
							<CTA
								key={index}
								tag="button"
								size="large"
								emphasis={emphases[index]}
								onClick={(event) => handleClick(index, event)}
								stretchContent
							>
								<Text appearance={TokenTextAppearance.bigcopy100} bold>
									{cta.label}
								</Text>
							</CTA>
						))}
					</StyledContainer>
				</Container>
			)}
		</Container>
	);
};
